import type { DisciplineBookingsInfo } from './interfaces'

import { Status as CustomerStatus } from '@brighterly/lib-core-types/customer/enums'

import { useDisciplineStore } from './store'
import { useDisciplinePrivileges } from './discipline-privileges.hook'
import { getSkippedLessonsCount } from './discipline-general-info'

interface DisciplineBalanceTooltips {
  pace: string
  unscheduled: string
  confirmed: string
  reserved: string
  completed: string
}

interface DisciplineGeneralBalanceReturn {
  refPace: ComputedRef<number>
  refUnscheduled: ComputedRef<number>
  refBookingsInfo: ComputedRef<DisciplineBookingsInfo>
  refSkipped: ComputedRef<number>
  refSkippedTooltip: ComputedRef<string>
  refIsWeeklyScheduleVisible: ComputedRef<boolean>
  tooltips: DisciplineBalanceTooltips
}

const EMPTY_BOOKINGS_INFO: DisciplineBookingsInfo = {
  confirmed: 0,
  reserved: 0,
  completed: 0,
  no_show: 0,
  canceled: 0,
  rescheduled: 0,
}

const TOOLTIPS: DisciplineBalanceTooltips = {
  pace: 'Max amount of\nlessons per week\nfor this child',
  unscheduled: 'Amount of lessons that\nare paid and committed,\nbut not yet confirmed\nor scheduled with the tutor',
  confirmed: 'All scheduled and\nconfirmed lessons\nfor this discipline',
  reserved: 'Future lessons\nreserved with a\ntutor. These will be\nconfirmed each\nmonth after rebill',
  completed: 'Amount of lessons\nfrom last purchase\nwith status\n"Completed"',
}

export const useDisciplineGeneralBalance = (): DisciplineGeneralBalanceReturn => {
  const disciplineStore = useDisciplineStore()
  const { canViewDisciplines } = useDisciplinePrivileges()

  const refPace = computed<number>(() => disciplineStore.getDetails?.pace ?? 0)
  const refUnscheduled = computed<number>(() => disciplineStore.getDetails?.unscheduled_bookings ?? 0)
  const refBookingsInfo = computed<DisciplineBookingsInfo>(() =>
    disciplineStore.getDetails?.bookings_info ?? EMPTY_BOOKINGS_INFO)
  const refSkipped = computed<number>(() => getSkippedLessonsCount(refBookingsInfo.value))
  const refSkippedTooltip = computed<string>(() => {
    const { no_show: noShow, canceled, rescheduled } = refBookingsInfo.value
    return `No show: ${noShow}\nCanceled: ${canceled}\nRescheduled: ${rescheduled}`
  })

  /** Only a DEAL customer has a weekly schedule, as in legacy. */
  const refIsWeeklyScheduleVisible = computed<boolean>(() =>
    canViewDisciplines.value && disciplineStore.getDetails?.customer?.status === CustomerStatus.Deal)

  return {
    refPace,
    refUnscheduled,
    refBookingsInfo,
    refSkipped,
    refSkippedTooltip,
    refIsWeeklyScheduleVisible,
    tooltips: TOOLTIPS,
  }
}
